<template>
  <div id="app" class="app-grid">
    <div class="console">
      <h2>指令修仙</h2>
      <div class="output" ref="output">
        <div v-for="(line, idx) in outputLines" :key="idx" class="out-line">
          <template v-for="(part, pidx) in tokenizeCommandText(line.text)">
            <button :key="pidx" v-if="part.isCmd" class="cmd-link" @click="runSuggested(part.actualCmd || part.text)">{{ part.text
            }}</button>
            <span :key="pidx + '-t'" v-else :class="['out-text', line.type || 'response']">{{ part.text }}</span>
          </template>
        </div>
      </div>
      <div class="input-row">
        <input v-model="commandInput" @keyup.enter="sendCommand" @keydown="onInputKey" placeholder="输入命令（直接发送查看帮助）"
          ref="input" :disabled="needsName" />
        <button @click="sendCommand">发送</button>
        <button @click="clearOutput">清除</button>
        <button @click="showAdminPanel" class="admin-btn">GM</button>

        <!-- suggestions moved inside input-row so they can be positioned above the input -->
        <div v-if="showSuggestions" class="suggestions">
          <div v-for="(item, idx) in suggestions" :key="idx" class="item" @click="acceptSuggestion(item)">
            {{ item }}
          </div>
        </div>
      </div>
      <!-- Naming modal component -->
      <NameModal
        :show-name-modal="needsName"
        @message="handleMessage"
        @name-submitted="handleNameSubmitted"
      />
      <!-- Item info modal -->
      <div v-if="showItemInfoModal" class="item-info-modal" @click="closeItemInfo">
        <div class="item-info-card" @click.stop>
          <h3>物品信息</h3>
          <p>{{ itemInfoText }}</p>
          <button @click="closeItemInfo">关闭</button>
        </div>
      </div>
      <!-- Sect Modal Component -->
      <SectModal
        :game-state="gameState"
        @sect-result="handleSectResult"
      />
      <!-- Exploration Modal Component -->
      <ExplorationModal
        ref="explorationModal"
        v-if="gameState.inExplorationModal"
        :game-state="gameState"
        @exploration-choice="handleExplorationChoice"
        @continue-exploration="handleContinueExploration"
        @end-exploration="handleEndExploration"
        @check-random-event="handleCheckRandomEvent"
        @start-exploration="handleStartExploration"
        @close-modal="handleCloseModal"
      />
      <!-- Battle Modal Component -->
      <BattleModal
        :show-battle-modal="gameState.inBattleModal"
        :battle-state="gameState.battleState"
        :player-name="gameState.name"
        :player-skills="getPlayerSkills()"
        @use-skill="handleBattleSkill"
        @attempt-flee="handleBattleFlee"
        @close-modal="handleCloseBattleModal"
      />
      <!-- Travel Modal Component -->
      <TravelModal
        v-if="gameState.inTravelModal"
        :game-state="gameState"
        @travel-result="handleTravelResult"
        @close-modal="handleCloseTravelModal"
      />
      <!-- State Modal Component -->
      <StateModal
        :game-state="gameState"
        :state-manager="gameController ? gameController.stateManager : null"
      />
      <!-- Bag Modal Component -->
      <BagModal
        v-if="gameState.inBagModal"
        :game-state="gameState"
        @show-item-info="showItemInfo"
        @use-item="handleBagItemUse"
        @close-modal="handleCloseBagModal"
      />
      <!-- Treasure Hall Modal Component -->
      <TreasureHallModal
        v-if="gameState.inTreasureHall"
        :game-state="gameState"
        @purchase-result="handlePurchaseResult"
        @save-state="saveGameState"
      />

      <!-- Admin Panel Component -->
      <AdminPanel
        :show-admin-modal="showAdminModal"
        @close-modal="showAdminModal = false"
      />

      <!-- Toast Notification -->
      <div v-if="toastMessage" class="toast-notification" :class="toastType">
        <div class="toast-content">
          <span class="toast-icon">{{ toastIcon }}</span>
          <span class="toast-text">{{ toastMessage }}</span>
        </div>
      </div>
    </div>

    <div class="game-area">
      <!-- Scene Info Panel (always rendered, shows current location info) -->
      <SceneInfoPanel
        :game-state="gameState"
        @execute-command="handleExecuteCommand"
        @show-message="handleShowMessage"
      />
      <!-- Forbidden Map Component (always rendered, handles its own visibility) -->
      <ForbiddenMap
        :game-state="gameState"
        :scanned-cells="scannedCells"
        @command="sendDirectCommand"
      />
      <!-- Pill Refining Component -->
      <PillRefining
        :game-state="gameState"
        @refine-result="handleRefineResult"
      />
      <!-- Treasure Refining Component -->
      <TreasureRefining
        :game-state="gameState"
        @refine-result="handleRefineResult"
      />
      <!-- Phaser Game Container (when not in forbidden map or refining) -->
      <div v-if="!gameState.inForbiddenMap && !gameState.inRefiningPill && !gameState.inRefiningTreasure" id="game-container"></div>
    </div>
  </div>
</template>

<script>
// import { startPhaser } from './game-vue';
import { parseCommand, suggest, state } from './cmd-world';
import gameController from './modules/game-controller/GameController.js';
import Vue from 'vue';
import { NameModal } from './modules/naming';
import { ForbiddenMap } from './modules/forbidden';
import { PillRefining, TreasureRefining } from './modules/refining';
import SectModal from './modules/sects/SectModal.vue';
import ExplorationModal from './modules/exploration/ExplorationModal.vue';
import BattleModal from './modules/exploration/BattleModal.vue';
import TravelModal from './modules/commands/TravelModal.vue';
import StateModal from './modules/state/StateModal.vue';
import BagModal from './modules/commands/BagModal.vue';
import TreasureHallModal from './modules/commands/TreasureHallModal.vue';
import SceneInfoPanel from './modules/SceneInfoPanel.vue';
import { COMMAND_MAPPINGS, getChineseCommand, getEnglishCommand } from './modules/config/commands.js';
import AdminPanel from './modules/AdminPanel.vue';

export default {
  name: 'App',
  components: {
    NameModal,
    ForbiddenMap,
    PillRefining,
    TreasureRefining,
    SectModal,
    ExplorationModal,
    BattleModal,
    TravelModal,
    StateModal,
    BagModal,
    TreasureHallModal,
    SceneInfoPanel,
    AdminPanel
  },
  data() {
    const gameState = Vue.observable(gameController.getGameState());
    // Reset modal states on page load to prevent auto-opening
    const modalNames = ['inStatusModal', 'inExplorationModal', 'inSectModal', 'inTravelModal', 'inBagModal', 'inForbiddenMap', 'inRefiningPill', 'inRefiningTreasure', 'inBattleModal', 'inTreasureHall'];
    modalNames.forEach(name => {
      gameState[name] = false;
    });

    return {
      commandInput: '',
      showNameModal: !(state && state.isNamed),
      outputLines: [{ type: 'system', text: '欢迎来到修仙世界控制台。输入 帮助 获取命令。' }],
      history: [],
      historyIndex: -1,
      suggestions: [],
      showSuggestions: false,
      phaserLoading: false,
      showItemInfoModal: false,
      itemInfoText: '',
      gameState: gameState, // Make state reactive
      explorationManager: gameController.explorationManager, // Add exploration manager
      showRefiningModal: false,
      refiningType: '', // 'pill' or 'treasure'
      refiningItemName: '',
      showAdminModal: false,
      // Toast notification
      toastMessage: '',
      toastType: 'success',
      toastIcon: '✅',
      toastTimer: null,
      // Modal handlers mapping for unified processing
      modalHandlers: {
        'open_refine_modal': function(res) {
          const modalData = res[0];
          this.openRefiningModal(modalData.refineType, modalData.itemName);
        },
        'open_sect_modal': function() {
          // Sect modal is handled automatically by component visibility
        },
        'open_exploration_modal': function() {
          // Initialize exploration state and show modal
          this.explorationManager.startExploration();
          const latestState = gameController.getGameState();
          Vue.set(this.gameState, 'explorationState', latestState.explorationState);
          this.openModal('inExplorationModal');
        },
        'open_battle_modal': function() {
          // Start beast battle and show modal
          const battleState = this.explorationManager.startBeastBattle();
          if (battleState) {
            Vue.set(this.gameState, 'battleState', battleState);
            this.openModal('inBattleModal');
          }
        },
        'open_travel_modal': function() {
          // Show travel modal
          this.openModal('inTravelModal');
        },
        'open_bag_modal': function() {
          // Show bag modal
          this.openModal('inBagModal');
        },
        'open_status_modal': function() {
          // Status modal is handled automatically by component visibility
        },
        'open_treasure_hall': function() {
          // Show treasure hall modal
          this.openModal('inTreasureHall');
        },
        'treasure_hall': function() {
          // Show treasure hall modal
          this.openModal('inTreasureHall');
        }
      }
    };
  },
  computed: {
    needsName() {
      return this.showNameModal;
    },

    scannedCells() {
      return this.gameState.scannedCells || [];
    },

    // All command words for tokenization (with deduplication)
    allCommandWords() {
      const baseWords = ['help', 'status', 'explore', 'practice', 'learn', 'use', 'join', 'bag', 'travel', 'reset', 'cultivate', 'refine_pill', 'refine_treasure'];
      const chineseWords = Object.keys(COMMAND_MAPPINGS.chineseToEnglish).filter(word => word !== '信息');
      const englishWords = Object.values(COMMAND_MAPPINGS.englishToChinese).filter(word => word !== '信息');
      const aliasWords = Object.keys(COMMAND_MAPPINGS.aliases);
      return [...new Set([...baseWords, ...chineseWords, ...englishWords, ...aliasWords])];
    }
  },
  watch: {
    commandInput: 'updateSuggestions'
  },
  mounted() {
    // 异步启动 Phaser，避免阻塞加载
    // this.phaserLoading = true;
    // startPhaser('game-container').then(() => { this.phaserLoading = false; this.outputLines.push('Phaser 已加载。'); }).catch(err => { this.phaserLoading = false; this.outputLines.push('Phaser 加载失败：' + (err.message || err)); });
    // 如果尚未起名，提示用户起名
    if (!state.isNamed) {
      this.outputLines.push({ type: 'prompt', text: '首次进入请为角色起名：使用命令 name <你的名字>，例如：name 张三' });
      this.focusInput();
    } else {
      // make sure modal hidden if already named
      this.showNameModal = false;
      // welcome returning player if not already shown
      const welcomeText = `欢迎，${state.name}。输入 帮助 获取命令。`;
      const already = this.outputLines.some(l => String(l.text).includes(welcomeText));
      if (!already) {
        this.outputLines.push({ type: 'prompt', text: welcomeText });
        this.updateSuggestions();
        this.focusInput();
      }
    }

    // 启动战斗状态更新定时器
    this.startBattleUpdateTimer();
  },

  beforeDestroy() {
    // 清除定时器
    if (this.battleUpdateTimer) {
      clearInterval(this.battleUpdateTimer);
    }
  },
  methods: {
    // 智能滚动到新内容
    scrollToNewContent(contentStartIndex, scrollBefore, outputAdded) {
      this.$nextTick(() => {
        const outputEl = this.$refs.output;
        if (outputEl) {
          // 如果添加了新的输出内容
          if (outputAdded) {
            const newContentStartEl = outputEl.children[contentStartIndex];
            if (newContentStartEl) {
              // 新信息从可见区域顶部开始显示
              const rect = newContentStartEl.getBoundingClientRect();
              const containerRect = outputEl.getBoundingClientRect();
              outputEl.scrollTop = outputEl.scrollTop + (rect.top - containerRect.top);
            } else {
              // 回退到保持之前的滚动位置
              outputEl.scrollTop = scrollBefore;
            }
          }
        }
      });
    },

    // 统一错误消息添加
    addErrorMessage(message) {
      this.outputLines.push({ type: 'error', text: message });
    },

    // 统一响应消息添加
    addResponseMessage(message) {
      this.outputLines.push({ type: 'response', text: message });
    },

    // 统一模态框打开
    openModal(modalName) {
      Vue.set(this.gameState, modalName, true);
      this.saveGameState();
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // 统一模态框关闭
    closeModal(modalName) {
      Vue.set(this.gameState, modalName, false);
      this.saveGameState();
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // 保存游戏状态
    saveGameState() {
      // 将Vue对象同步回StateManager并保存
      gameController.stateManager.updateState(this.gameState);
    },

    // 同步最新状态到Vue对象
    syncLatestState() {
      const latestState = gameController.getGameState();
      // 深度同步状态，但保留Vue响应性
      Object.keys(latestState).forEach(key => {
        if (key !== 'inStatusModal' && key !== 'inBattleModal' && key !== 'battleState') {
          // 不同步模态框状态，避免意外关闭
          Vue.set(this.gameState, key, latestState[key]);
        }
      });
    },

    // 强制更新
    forceUpdate() {
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // 聚焦输入框
    focusInput() {
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },

    sendCommand() {
      let cmd = (this.commandInput || '').trim();
      if (!cmd) {
        cmd = 'help';
      }
      // if already named, prevent name command from being used again
      if (state.isNamed && /^name(\s|$)/i.test(cmd)) {
        this.addErrorMessage('你已起过名，无法再次起名。');
        this.commandInput = '';
        return;
      }
      // block other commands until player chooses a name
      if (!state.isNamed && !/^name(\s|$)/i.test(cmd) && cmd.toLowerCase() !== 'help') {
        this.outputLines.push({ type: 'prompt', text: '请先起名：使用 name <名字>，例如：name 少侠' });
        this.commandInput = '';
        return;
      }
      this.history.unshift(cmd);
      this.historyIndex = -1;

      // 记住点击前的滚动位置和新内容开始位置
      const el = this.$refs.output;
      const scrollBefore = el ? el.scrollTop : 0;
      const contentStartIndex = this.outputLines.length;

      // 初始化输出添加标志
      let outputAdded = false;

      // 显示用户输入（info命令不显示输入，因为它直接显示弹框）
      // if (!cmd.toLowerCase().startsWith('info ')) {
      //   this.outputLines.push({ type: 'command', text: cmd });
      //   outputAdded = true;
      // }
      // 处理命令并显示结果
      const preNamed = state.isNamed;
      try {
        const res = parseCommand(cmd);
        const { shouldAddOutput, hasOutput } = this.processCommandResult(cmd, res);
        outputAdded = shouldAddOutput;
        if (hasOutput) outputAdded = true;
      } catch (e) {
        this.outputLines.push({ type: 'error', text: '命令执行出错：' + (e.message || e) });
        outputAdded = true;
      }
      // if naming just completed, hide modal and prompt next steps
      if (!preNamed && state.isNamed) {
        this.showNameModal = false;
        this.outputLines.push({ type: 'prompt', text: `欢迎，${state.name}。输入 帮助 获取命令。` });
        this.updateSuggestions();
      }
      // 记录并清空输入
      this.commandInput = '';
      this.suggestions = [];
      this.showSuggestions = false;

      // 调用滚动方法
      this.scrollToNewContent(contentStartIndex, scrollBefore, outputAdded);
    },
    // Handle messages from NameModal component
    handleMessage(message) {
      this.addResponseMessage(message);
    },

    // Handle name submission from NameModal component
    handleNameSubmitted(name) {
      this.showNameModal = false;
      this.outputLines.push({ type: 'prompt', text: `欢迎，${name}。输入 帮助 获取命令。` });
      this.updateSuggestions();
      this.focusInput();
    },
    clearOutput() {
      this.outputLines = [];
    },

    showAdminPanel() {
      this.showAdminModal = true;
    },
    onInputKey(e) {
      // up/down 历史
      if (e.key === 'ArrowUp') {
        if (this.history.length === 0) return;
        this.historyIndex = Math.min(this.history.length - 1, (this.historyIndex === -1 ? 0 : this.historyIndex + 1));
        this.commandInput = this.history[this.historyIndex];
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowDown') {
        if (this.history.length === 0) return;
        if (this.historyIndex <= 0) { this.historyIndex = -1; this.commandInput = ''; } else { this.historyIndex -= 1; this.commandInput = this.history[this.historyIndex]; }
        e.preventDefault();
        return;
      }
      if (e.key === 'Tab') {
        // 接受第一个建议
        if (this.suggestions.length > 0) {
          this.commandInput = this.suggestions[0];
          this.suggestions = [];
          this.showSuggestions = false;
          e.preventDefault();
        }
        return;
      }
      // 其它键更新建议（延迟可选）
      this.updateSuggestions();
    },
    updateSuggestions() {
      try {
        const s = suggest(this.commandInput);
        this.suggestions = s.map(x => x);
        this.showSuggestions = this.suggestions.length > 0;
      } catch (e) {
        // 如果 suggest 未定义或出错，不阻塞 UI
        this.suggestions = [];
        this.showSuggestions = false;
      }
    },
    acceptSuggestion(item) {
      this.commandInput = item;
      this.suggestions = [];
      this.showSuggestions = false;
      this.focusInput();
    },
    _makeLine(raw) {
      // Handle object format messages (like item gain messages)
      if (raw && typeof raw === 'object') {
        if (raw.text) {
          // Legacy object format with text property
          return raw;
        } else if (raw.message) {
          // New object format with message property (item gain messages)
          return { type: 'response', text: raw.message };
        } else {
          // Unknown object format, convert to string
          raw = String(raw);
        }
      }

      const txt = String(raw || '');
      // heuristics for classification
      const lower = txt.toLowerCase();
      if (lower.startsWith('请先') || lower.includes('首次进入') || lower.includes('使用命令')) return { type: 'prompt', text: txt };
      if (lower.startsWith('未知') || lower.includes('出错') || lower.includes('失败') || lower.includes('无法')) return { type: 'error', text: txt };
      // Special handling for status output
      if (txt.includes('【角色信息】') || txt.includes('【属性面板】') || txt.includes('境界系统')) return { type: 'status-header', text: txt };
      if (txt.startsWith('姓名:') || txt.startsWith('门派:') || txt.startsWith('境界:') || txt.startsWith('修为:')) return { type: 'status-info', text: txt };
      if (txt.startsWith('气血:') || txt.startsWith('攻击:') || txt.startsWith('防御:') || txt.startsWith('灵力:') || txt.startsWith('神识强度:')) return { type: 'status-attr', text: txt };
      if (txt.startsWith('位置:')) return { type: 'status-location', text: txt };
      if (txt.includes('╔') || txt.includes('║') || txt.includes('╚') || txt.includes('┌') || txt.includes('│') || txt.includes('└')) return { type: 'status-frame', text: txt };
      if (txt.startsWith('□') || txt.startsWith('○')) return { type: 'status-realm', text: txt };
      if (txt.includes('[当前]') || txt.includes('█') || txt.includes('░')) return { type: 'status-current', text: txt };
      if (txt.includes('修炼之路漫漫')) return { type: 'status-footer', text: txt };
      return { type: 'response', text: txt };
    },

    // Split a line into parts and mark command-like tokens as clickable
    tokenizeCommandText(text) {
      try {
        // Handle object format messages (like item gain messages)
        if (text && typeof text === 'object') {
          if (text.message && typeof text.message === 'string') {
            text = text.message;
          } else {
            // If it's an object but doesn't have valid message, convert to string
            text = String(text);
          }
        }

        // Ensure text is a string at this point
        if (typeof text !== 'string') {
          text = String(text || '');
        }

        const t = text || '';

        // Double check that t is a string
        if (typeof t !== 'string') {
          console.warn('tokenizeCommandText received non-string input:', t);
          return [{ text: String(t), isCmd: false }];
        }

        // Get all command words including forbidden map commands if applicable
        let cmds = [...this.allCommandWords];
        if (this.gameState && this.gameState.inForbiddenMap) {
          cmds = cmds.concat(['up', 'down', 'left', 'right', 'scan', 'exit']);
        }

        // Regex to match command words (including Chinese)
        const cmdRe = new RegExp('(' + cmds.join('|') + ')', 'gi');

        const parts = [];
        let lastIndex = 0;
        let m;
        while ((m = cmdRe.exec(t)) !== null) {
          const idx = m.index;
          const cmd = m[1];
          if (idx > lastIndex) parts.push({ text: t.slice(lastIndex, idx), isCmd: false });

          // Extract command + arguments from current position to end of line
          const cmdStart = idx;
          const lineEnd = t.length;
          const cmdWithArgs = t.slice(cmdStart, lineEnd).trim();

          let actualCmd;
          if (cmdWithArgs.indexOf(',') !== -1) {
            // If contains comma, it's likely a command list, execute just the command
            actualCmd = getEnglishCommand(cmd) || cmd;
          } else {
            // Otherwise, it's command + arguments
            const englishCmd = getEnglishCommand(cmd);
            actualCmd = englishCmd ? englishCmd + cmdWithArgs.slice(cmd.length) : cmdWithArgs;
          }

          // 对于 help 输出中的命令，不附加参数
          if (cmdWithArgs.indexOf(',') !== -1) {
            // If contains comma, it's likely a command list, execute just the command
            parts.push({ text: cmd, actualCmd: actualCmd, isCmd: true });
          } else {
            // For single commands in help text, don't append extra text as arguments
            const cleanCmd = getEnglishCommand(cmd) || cmd;
            parts.push({ text: cmd, actualCmd: cleanCmd, isCmd: true });
          }
          lastIndex = idx + cmd.length;
        }
        if (lastIndex < t.length) parts.push({ text: t.slice(lastIndex), isCmd: false });
        // if no matches, return the whole text as a single non-command part
        if (parts.length === 0) return [{ text: t, isCmd: false }];
        return parts;
      } catch (error) {
        console.error('Error in tokenizeCommandText:', error, 'Input:', text);
        // Return safe fallback
        return [{ text: String(text || ''), isCmd: false }];
      }
    },

    // Execute a suggested command as if the user typed it and sent
    runSuggested(cmdText) {
      if (!cmdText) return;
      // If the command is Chinese, map it to English
      const actualCmd = getEnglishCommand(cmdText);
      // Directly execute the command instead of setting input
      this.executeCommand(actualCmd);
    },

    // Unified command result processing
    processCommandResult(cmd, res) {
      let shouldAddOutput = false;
      let hasOutput = false;

      // Check for beast encounter in the result
      if (Array.isArray(res) && res.length > 0) {
        const beastEncounterObj = res.find(item => item && typeof item === 'object' && item.type === 'beast_encounter');
        if (beastEncounterObj) {
          // Start battle
          const battleState = this.explorationManager.startBeastBattle();
          if (battleState) {
            Vue.set(this.gameState, 'battleState', battleState);
            this.openModal('inBattleModal');
          }
          // Show the encounter messages
          if (beastEncounterObj.messages && Array.isArray(beastEncounterObj.messages)) {
            beastEncounterObj.messages.forEach(msg => this.outputLines.push(this._makeLine(msg)));
            hasOutput = true;
          }
          return { shouldAddOutput, hasOutput: true };
        }
      }

      // Special handling for different command types
      if (cmd.toLowerCase().startsWith('info ')) {
        // Info command shows popup
        if (Array.isArray(res) && res.length > 0) {
          this.showItemInfo(res[0]);
        } else if (typeof res === 'string') {
          this.showItemInfo(res);
        }
      } else if (Array.isArray(res) && res.length > 0 && this.modalHandlers[res[0].type]) {
        // Handle modal commands using the handlers mapping
        this.modalHandlers[res[0].type].call(this, res);
        // Don't show any output for modal commands
      } else {
        // Normal command output
        if (Array.isArray(res)) {
          res.forEach(r => this.outputLines.push(this._makeLine(r)));
          hasOutput = res.length > 0;
          // Special handling for exploration commands - refresh exploration state for continued exploration
          if (cmd.toLowerCase().startsWith('explore ') && res.length > 0 && !res.some(r => String(r).includes('探索结束'))) {
            // If it's an exploration choice (not ending exploration), refresh the exploration state
            setTimeout(() => {
              // Refresh exploration state by calling startExploration again
              this.explorationManager.startExploration();
              const latestState = gameController.getGameState();
              Vue.set(this.gameState, 'explorationState', latestState.explorationState);
              this.forceUpdate();
            }, 100); // Small delay to allow output to be displayed first
          }
        } else if (typeof res === 'string') {
          this.outputLines.push(this._makeLine(res));
          hasOutput = true;
        }
      }

      return { shouldAddOutput, hasOutput };
    },

    // Execute a command directly without setting input
    executeCommand(cmd) {
      // block other commands until player chooses a name
      if (!state.isNamed && !/^name(\s|$)/i.test(cmd) && cmd.toLowerCase() !== 'help') {
        this.outputLines.push({ type: 'prompt', text: '请先起名：使用 name <名字>，例如：name 少侠' });
        return;
      }

      // 记住点击前的滚动位置和新内容开始位置
      const el = this.$refs.output;
      const scrollBefore = el ? el.scrollTop : 0;
      const contentStartIndex = this.outputLines.length;

      // 初始化输出添加标志
      let outputAdded = false;

      // 处理命令并显示结果
      try {
        const res = parseCommand(cmd);
        const { shouldAddOutput, hasOutput } = this.processCommandResult(cmd, res);
        outputAdded = shouldAddOutput;
        if (hasOutput) outputAdded = true;
      } catch (e) {
        this.outputLines.push({ type: 'error', text: '命令执行出错：' + (e.message || e) });
        outputAdded = true;
      }

      // 调用滚动方法
      this.scrollToNewContent(contentStartIndex, scrollBefore, outputAdded);
    },

    // Execute a direct command for forbidden area buttons
    sendDirectCommand(cmd) {
      if (!cmd) return;
      this.commandInput = cmd;
      this.sendCommand();
    },

    // Show item info in a popup modal
    showItemInfo(text) {
      this.itemInfoText = String(text || '');
      this.showItemInfoModal = true;
    },

    // Close the item info modal
    closeItemInfo() {
      this.showItemInfoModal = false;
      this.itemInfoText = '';
    },

    // Open refining modal
    openRefiningModal(refineType, itemName) {
      // 关闭所有炼制界面
      this.gameState.inRefiningPill = false;
      this.gameState.inRefiningTreasure = false;
      this.gameState.refiningItemName = '';

      if (refineType === 'pill') {
        this.gameState.inRefiningPill = true;
        this.gameState.refiningItemName = itemName;
      } else if (refineType === 'treasure') {
        this.gameState.inRefiningTreasure = true;
        this.gameState.refiningItemName = itemName;
      }
    },

    // Handle refine result from components
    handleRefineResult(message) {
      this.addResponseMessage(message);
    },

    // Handle sect result from SectModal component
    handleSectResult(message) {
      this.addResponseMessage(message);
    },

    // Handle travel result from TravelModal component
    handleTravelResult(message) {
      if (Array.isArray(message)) {
        message.forEach(msg => this.addResponseMessage(msg));
      } else {
        this.addResponseMessage(message);
      }
    },

    // Handle exploration choice from ExplorationModal component
    handleExplorationChoice(choice) {
      try {
        const res = parseCommand('explore ' + choice);

        // Check for beast encounter in the exploration manager
        if (this.explorationManager.hasPendingBeastEncounter()) {
          // Close exploration modal first
          this.closeModal('inExplorationModal');
          const battleState = this.explorationManager.startBeastBattle();
          if (battleState) {
            Vue.set(this.gameState, 'battleState', battleState);
            // Use setTimeout to ensure exploration modal is closed first
            setTimeout(() => {
              this.openModal('inBattleModal');
            }, 100);
          }
        }

        // Process results for display
        if (this.$refs.explorationModal && Array.isArray(res) && res.length > 0) {
          // Filter out only the meaningful result messages (not system messages)
          const displayResults = res.filter(r => {
            // Handle object format messages (new format)
            if (typeof r === 'object' && r !== null && r.message) {
              return true;
            }
            // Handle string format messages (legacy format)
            if (typeof r === 'string') {
              return !r.includes('你可以：') && // Remove option headers
                     r.trim().length > 0 &&
                     // Allow scene headers and item gain messages, but filter out option lists
                     (!r.match(/^\d+\.\s/) || r.includes('获得') || r.includes('发现') || r.includes('找到') || r.includes('采集到'));
            }
            return false;
          });

          if (displayResults.length > 0) {
            this.$refs.explorationModal.explorationResults = displayResults;
            this.$refs.explorationModal.$forceUpdate();
          }
        }
      } catch (e) {
        console.error('Error in handleExplorationChoice:', e);
        this.addErrorMessage('探索出错：' + (e.message || e));
      }
    },

    // Handle continue exploration from ExplorationModal component
    handleContinueExploration() {
      // Continue exploration by refreshing the state without console output
      this.explorationManager.startExploration();
      const latestState = gameController.getGameState();
      // Update the existing gameState object with latest data
      Vue.set(this.gameState, 'explorationState', latestState.explorationState);
      // Force update to ensure Vue detects the change
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // Handle end exploration from ExplorationModal component
    handleEndExploration() {
      // End exploration by choosing 0
      this.executeCommand('explore 0');
    },

    // Handle check random event from ExplorationModal component
    handleCheckRandomEvent() {
      // Check for random events using the exploration manager
      const result = this.explorationManager.checkAndTriggerRandomEvent();
      if (result) {
        // 有随机事件触发，返回事件信息
        return result;
      }
      return null;
    },

    // Handle start exploration from ExplorationModal component
    handleStartExploration() {
      // Start exploration by calling startExploration method
      this.explorationManager.startExploration();
      const latestState = gameController.getGameState();
      // Update the existing gameState object with latest data
      Vue.set(this.gameState, 'explorationState', latestState.explorationState);
      // Force update to ensure Vue detects the change
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // Handle close modal from ExplorationModal component
    handleCloseModal() {
      Vue.set(this.gameState, 'inExplorationModal', false);
      // Force update to ensure Vue detects the change
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // Handle close modal from TravelModal component
    handleCloseTravelModal() {
      Vue.set(this.gameState, 'inTravelModal', false);
      // Force update to ensure Vue detects the change
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // Handle bag item use from BagModal component
    handleBagItemUse(itemName) {
      // Execute use command for the item
      this.executeCommand('use ' + itemName);
    },

    // Handle close modal from BagModal component
    handleCloseBagModal() {
      Vue.set(this.gameState, 'inBagModal', false);
      // Force update to ensure Vue detects the change
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // Handle purchase result from TreasureHallModal component
    handlePurchaseResult(message) {
      this.showToast(message, 'success');
    },

    // Show toast notification
    showToast(message, type = 'success') {
      this.toastMessage = message;
      this.toastType = type;

      // Set appropriate icon
      switch (type) {
        case 'success':
          this.toastIcon = '✅';
          break;
        case 'error':
          this.toastIcon = '❌';
          break;
        case 'warning':
          this.toastIcon = '⚠️';
          break;
        default:
          this.toastIcon = 'ℹ️';
      }

      // Clear existing timer
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
      }

      // Auto hide after 3 seconds
      this.toastTimer = setTimeout(() => {
        this.toastMessage = '';
        this.toastTimer = null;
      }, 3000);
    },

    // Handle battle skill use from BattleModal component
    handleBattleSkill(skill) {
      const availableSkills = this.getPlayerSkills();
      const result = this.explorationManager.executeBattleAction('attack', skill, availableSkills);
      if (result.success) {
        // Update battle state
        const battleState = this.explorationManager.getBattleState();
        if (battleState) {
          Vue.set(this.gameState, 'battleState', battleState);
          this.$forceUpdate(); // 强制更新组件

          // If battle ended, close modal after a delay
          if (battleState.battleResult) {
            setTimeout(() => {
              this.handleCloseBattleModal();
              // Refresh player state after battle
              const latestState = gameController.getGameState();
              Object.assign(this.gameState, latestState);
              this.forceUpdate();
            }, 3000);
          }
        }
      } else {
        this.addErrorMessage(result.message);
      }
    },

    // Handle battle flee from BattleModal component
    handleBattleFlee() {
      const result = this.explorationManager.executeBattleAction('flee');
      if (result.success) {
        // Update battle state
        const battleState = this.explorationManager.getBattleState();
        if (battleState) {
          Vue.set(this.gameState, 'battleState', battleState);

          // Close modal after fleeing
          setTimeout(() => {
            this.handleCloseBattleModal();
            // Refresh player state after battle
            const latestState = gameController.getGameState();
            Object.assign(this.gameState, latestState);
            this.forceUpdate();
          }, 2000);
        }
      } else {
        this.addErrorMessage(result.message);
      }
    },

    // Handle close modal from BattleModal component
    handleCloseBattleModal() {
      Vue.set(this.gameState, 'inBattleModal', false);
      Vue.set(this.gameState, 'battleState', null);

      // 强制同步最新状态到Vue对象
      this.syncLatestState();

      // Force update to ensure Vue detects the change
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // Start battle state update timer
    startBattleUpdateTimer() {
      this.battleUpdateTimer = setInterval(() => {
        if (this.gameState.inBattleModal && this.explorationManager.isInBattle()) {
          // Update battle state every 500ms during battle
          const battleState = this.explorationManager.getBattleState();
          if (battleState && JSON.stringify(battleState) !== JSON.stringify(this.gameState.battleState)) {
            this.gameState.battleState = battleState;
            this.$forceUpdate();
          }
        }
      }, 500);
    },

    // Get player skills for battle
    getPlayerSkills() {
      // Convert skills object to array if needed
      let skillsArray = [];
      if (this.gameState.skills) {
        if (Array.isArray(this.gameState.skills)) {
          skillsArray = this.gameState.skills;
        } else if (typeof this.gameState.skills === 'object') {
          skillsArray = Object.keys(this.gameState.skills);
        }
      }

      // Filter out '炼丹术' and '炼器术' from battle skills
      skillsArray = skillsArray.filter(skill => skill !== '炼丹术' && skill !== '炼器术');

      // If no skills left, provide basic attack
      if (skillsArray.length === 0) {
        skillsArray = ['攻击'];
      }

      return skillsArray;
    },

    // Handle execute command from SceneInfoPanel component
    handleExecuteCommand(command) {
      // Handle special commands from scene panel
      if (command === 'treasure_hall') {
        this.openModal('inTreasureHall');
        return;
      }
      // Execute other commands directly
      this.executeCommand(command);
    },

    // Handle show message from SceneInfoPanel component
    handleShowMessage(message) {
      // Add message to output console
      this.addResponseMessage(message);
    }


  }
};
</script>

<style scoped>
.app-grid {
  height: 100%;
}

.console {
  background: #0f1720;
  color: #dbeafe;
  padding: 12px;
  height: 40%;
  display: flex;
  flex-direction: column;
}

.console h2 {
  margin: 0 0 8px 0;
  font-size: 16px;
}

.output {
  background: #071328;
  flex: 1;
  padding: 8px;
  overflow: auto;
  border-radius: 4px;
  border: 1px solid #102a43;
}

.out-line {
  white-space: pre-wrap;
  line-height: 18px;
}

.out-text.command {
  color: #00e5ff;
  /* cyan for user commands */
}

.out-text.response {
  color: #e6f3d6;
  /* light green for normal responses */
}

.out-text.prompt {
  color: #ffd166;
  /* warm yellow/orange for prompts/hints */
}

.out-text.error {
  color: #ff6b6b;
  /* red for errors */
}

.out-text.system {
  color: #aab7c6;
  /* muted gray for system */
}

/* Status display styles */
.out-text.status-header {
  color: #fbbf24;
  font-weight: bold;
  font-size: 14px;
  text-align: center;
  margin: 8px 0 4px 0;
  text-shadow: 0 0 4px rgba(251, 191, 36, 0.5);
}

.out-text.status-info {
  color: #60a5fa;
  font-weight: 500;
  padding-left: 8px;
}

.out-text.status-attr {
  color: #34d399;
  display: inline-block;
  padding-left: 8px;
  min-width: 120px;
}

.out-text.status-location {
  color: #a78bfa;
  font-style: italic;
  padding-left: 8px;
}

.out-text.status-frame {
  color: #f59e0b;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  text-align: center;
}

.out-text.status-realm {
  color: #fbbf24;
  margin-left: 8px;
}

.out-text.status-current {
  color: #ef4444;
  font-weight: bold;
  background: linear-gradient(90deg, rgba(239, 68, 68, 0.1) 0%, transparent 100%);
  padding: 2px 4px;
  border-radius: 2px;
}

.out-text.status-footer {
  color: #94a3b8;
  font-style: italic;
  text-align: center;
  margin-top: 8px;
  font-size: 12px;
}

/* clickable command links inside outputs */
.cmd-link {
  background: transparent;
  border: none;
  color: #00e5ff;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin: 0 2px;
  font: inherit;
}

.cmd-link:hover {
  color: #7efcff;
}

.cmd-link:active {
  transform: translateY(1px);
}

.input-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  position: relative;
}

.input-row input {
  flex: 1;
  padding: 6px 8px;
  border-radius: 4px;
  border: 1px solid #22303f;
  background: #081425;
  color: #e6eef8;
}

.input-row button {
  padding: 6px 10px;
  border-radius: 4px;
  border: none;
  background: #0ea5a2;
  color: #042f2e;
  cursor: pointer;
}

.admin-btn {
  background: #dc2626 !important;
  color: white !important;
}

.admin-btn:hover {
  background: #b91c1c !important;
}



.suggestions {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  width: 100%;
  background: #071328;
  border: 1px solid #123;
  color: #dbeafe;
  border-radius: 4px;
  max-height: 160px;
  overflow: auto;
  z-index: 50;
  box-sizing: border-box;
}

.suggestions .item {
  padding: 6px 8px;
  cursor: pointer;
}

.suggestions .item:hover {
  background: #0e4866;
}



/* item info modal */
.item-info-modal {
  position: absolute;
  inset: 0px 0px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.6);
  z-index: 260;
}

.item-info-card {
  background: #071328;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #123;
  color: #dbeafe;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.item-info-card h3 {
  margin: 0;
  font-size: 18px;
}

.item-info-card p {
  margin: 0;
  line-height: 1.5;
}

.item-info-card button {
  align-self: flex-end;
  padding: 6px 12px;
  border-radius: 4px;
  border: none;
  background: #0ea5a2;
  color: #042f2e;
  cursor: pointer;
}

.game-area {
  background: #071329;
  color: #e6eef8;
  padding: 12px;
  height: calc(60% - 12px);
}

#game-container {
  width: 100%;
  border: 2px solid #22303f;
  background: #000;
}



@media (max-width: 900px) {

  /* 移动设备保持左右栅格对齐，但调整比例和尺寸 */
  .app-grid {
    grid-template-columns: 1fr 1fr;
    /* 保持两列 */
    gap: 8px;
    /* 减少间距 */
  }

  .console {
    height: 40%;
    /* 减少控制台高度 */
    padding: 8px;
    /* 减少内边距 */
  }

  .console h2 {
    font-size: 14px;
    /* 减小标题字体 */
    margin-bottom: 6px;
  }

  .output {
    padding: 6px;
    /* 减少输出区域内边距 */
    font-size: 12px;
    /* 减小字体大小 */
  }

  .input-row {
    margin-top: 6px;
    gap: 6px;
  }

  .input-row input {
    padding: 4px 6px;
    /* 减小输入框内边距 */
    font-size: 12px;
  }

  .input-row button {
    padding: 4px 8px;
    /* 减小按钮内边距 */
    font-size: 12px;
  }

  .game-area {
    height: calc(60% - 8px);
    /* 调整游戏区域高度 */
    padding: 8px;
    /* 减少内边距 */
  }

  /* 移动设备上调整status显示样式 */
  .out-text.status-header {
    font-size: 12px;
    margin: 6px 0 3px 0;
  }

  .out-text.status-info,
  .out-text.status-attr,
  .out-text.status-location {
    padding-left: 8px;
    font-size: 11px;
  }

  .out-text.status-attr {
    padding-left: 8px;
    min-width: 100px;
  }

  .out-text.status-frame {
    font-size: 9px;
    line-height: 1.2;
  }

  .out-text.status-realm {
    font-size: 11px;
    padding-left: 8px;
    display: block;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: calc(100vw - 40px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .out-text.status-current {
    font-size: 10px;
    padding: 1px 2px;
    display: inline-block;
    max-width: calc(100vw - 60px);
    overflow: hidden;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .out-text.status-footer {
    font-size: 10px;
    margin-top: 6px;
  }
}

/* Toast Notification */
.toast-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  min-width: 300px;
  max-width: 400px;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: toastSlideIn 0.3s ease-out;
}

.toast-notification.success {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%);
  border-color: rgba(16, 185, 129, 0.3);
}

.toast-notification.error {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%);
  border-color: rgba(239, 68, 68, 0.3);
}

.toast-notification.warning {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.9) 0%, rgba(217, 119, 6, 0.9) 100%);
  border-color: rgba(245, 158, 11, 0.3);
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
}

.toast-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.toast-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

@keyframes toastSlideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 600px) {

  /* 小屏幕设备进一步优化 */
  .app-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    /* 改为垂直布局 */
  }

  .console {
    height: 40%;
  }

  .game-area {
    height: calc(60% - 12px);
  }

  .toast-notification {
    left: 10px;
    right: 10px;
    min-width: auto;
    top: 10px;
  }
}
</style>
